import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useMieSquadre, useSquadre } from "../../hooks/useFase7";
import { useBudgetTotale } from "../../hooks/useFase2";
import { useStagioneAttiva } from "../../hooks/useStagione";
import {
  useClassifica,
  useCompetizioniStagione,
  usePartiteGiornata,
} from "../../hooks/useFase5";
import { useAuthStore } from "../../lib/auth-store";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { BudgetHeroCard } from "./BudgetHeroCard";
import { AndamentoCard } from "./AndamentoCard";
import { ProssimaSfida } from "./ProssimaSfida";
import { IncontroConcluso } from "./IncontroConcluso";
import { faseGiornate } from "./faseGiornate";
import { ClassificaCard } from "./ClassificaCard";
import { VittoriaBanner } from "./VittoriaBanner";
import { InstallaAppBanner } from "../common/InstallaApp";
import { TrofeiSquadra } from "../common/TrofeiSquadra";
import { MagliaSquadra } from "../common/MagliaSquadra";
import { LogoSquadra } from "../common/LogoSquadra";
import { useCartello } from "../../hooks/useScommesse";
import { tr } from "../../i18n";

interface GiornataLight {
  id: string;
  stagione_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
  /** Primo fischio SA della giornata (calendario locale, sync football-data). */
  data_inizio_partite_sa: string | null;
}

interface VersionInfo {
  version: string;
  environment: string;
  allow_initial_setup: boolean;
}

const ADMIN_ROLES = new Set(["presidente_lega", "admin", "tesoriere", "mod_tm", "mod_mercato"]);

export function DashboardPage() {
  const { user } = useAuth();
  const { data: squadre } = useSquadre();
  // Andrea 2026-09-01: include anche le squadre gestite da allenatore in
  // seconda, non solo quelle da presidente (useMieSquadre le unisce).
  const { data: mieSquadre } = useMieSquadre(user?.id);
  const miaSquadra = mieSquadre[0];
  // Verifica se l'utente è admin (per mostrare shortcut "Gestione")
  const userRoles = new Set<string>([user?.ruolo_lega ?? "", ...(user?.ruoli_aggiuntivi ?? [])]);
  const isAdminCapable = Array.from(userRoles).some((r) => ADMIN_ROLES.has(r));

  const { data: version } = useQuery({
    queryKey: ["version"],
    queryFn: async () => (await api.get<VersionInfo>("/version")).data,
  });

  // Budget cumulativo lifetime della mia squadra (cross-stagione)
  const { data: budgetTotale } = useBudgetTotale(miaSquadra?.id ?? null);
  const saldoMilioni = budgetTotale?.saldo_milioni ? Number(budgetTotale.saldo_milioni) : null;

  // Stagione attiva (anche in preparazione/mercato) per countdown formazione
  const { data: stagioneAttivaInfo } = useStagioneAttiva();
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const stagioneAttivaId = stagioneAttivaInfo?.id ?? null;
  const { data: giornate } = useQuery({
    queryKey: ["stagione-giornate", stagioneAttivaId],
    queryFn: async () =>
      (await api.get<GiornataLight[]>(`/admin/stagioni/${stagioneAttivaId}/giornate`)).data,
    enabled: isAuth && !!stagioneAttivaId,
  });
  // Orologio a 30 secondi: quando scatta la deadline la home passa da
  // sola al blocco "in corso" senza ricaricare.
  const [adesso, setAdesso] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setAdesso(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  // Andrea 2026-09-11: giornata in corso (turno partito, non calcolata),
  // prossima da schierare, ultima calcolata — vedi faseGiornate.ts.
  const {
    inCorso: giornataInCorso,
    prossima: prossimaGiornata,
    ultimaCalcolata: ultimaGiornataCalcolata,
  } = faseGiornate(giornate, adesso);

  // Competizioni della stagione → trova la prima competizione "attiva" per pescare
  // la prossima sfida della MIA squadra
  const { data: competizioniStagione } = useCompetizioniStagione(stagioneAttivaId);
  // Tutte quelle non concluse: campionato, coppa, eventuale supercoppa.
  // Andrea 2026-09-09: la home le sfoglia una per volta.
  const competizioniInCorso = (competizioniStagione ?? []).filter((c) => c.stato !== "conclusa");
  const primaCompetizione = competizioniInCorso[0] ?? null;
  const { data: classificaPrimaComp } = useClassifica(primaCompetizione?.id ?? null);
  // Mie partite appena concluse (ultima giornata calcolata): Christian
  // 2026-11-03, tutte le competizioni — campionato e coppa — nell'ordine
  // della stagione, non solo la prima.
  const { data: partiteUltimaCalcolata } = usePartiteGiornata(ultimaGiornataCalcolata?.id ?? null);
  const ordineComp = (competizioniStagione ?? []).map((c) => c.id);
  const mieConcluse = (partiteUltimaCalcolata ?? [])
    .filter(
      (p) =>
        !!miaSquadra &&
        !p.obsoleto &&
        p.punteggio_casa !== null &&
        p.punteggio_casa !== undefined &&
        (p.squadra_casa_id === miaSquadra.id || p.squadra_trasferta_id === miaSquadra.id),
    )
    .sort((a, b) => ordineComp.indexOf(a.competizione_id) - ordineComp.indexOf(b.competizione_id));
  const nomeCompetizioneById = (id: string): string =>
    competizioniStagione?.find((c) => c.id === id)?.nome ?? "";
  const nomeSquadraById = (id: string): string =>
    squadre?.find((s) => s.id === id)?.nome ?? id.slice(0, 8);
  // Andrea 2026-05-20 — logo personalizzato della squadra (data-URL).
  // null se la squadra non l'ha caricato → TeamBadge mostra le iniziali.
  const logoSquadraById = (id: string): string | null =>
    squadre?.find((s) => s.id === id)?.logo_data_url ?? null;

  // Scommesse aperte della stagione (per il blocco sotto la classifica).
  const { data: cartelliStagione } = useCartello({
    stagioneId: stagioneAttivaId,
  });
  const scommesseAperte = (cartelliStagione ?? []).filter(
    (c) => c.stato === "aperta" && !c.chiusa,
  ).length;

  const firstName = user?.nome_completo?.trim().split(" ")[0] ?? "";

  // Label posizione/info squadra (riusata nel layout mobile + desktop).
  // Andrea 2026-06-15: prima del campionato tr("In attesa 1ª giornata"), poi
  // sempre posizione in classifica + punti.
  const posLabel: string = (() => {
    if (!miaSquadra) return "";
    const miaRiga = classificaPrimaComp?.find((r) => r.squadra_id === miaSquadra.id);
    if (miaRiga)
      return tr("{{pos}}ª · {{punti}} pt", { pos: miaRiga.posizione, punti: miaRiga.punti });
    return tr("In attesa 1ª giornata");
  })();

  // Label dinamica nella greeting bar — fonte di verita': useStagioneAttiva
  // (che gia' include preparazione / mercato_* / in_corso). Andrea 2026-05-18:
  // tr("Pre-stagione · in attesa di setup") doveva sparire dopo la creazione
  // della stagione.
  const stagioneLabel: string = (() => {
    if (!stagioneAttivaInfo) return tr("Pre-stagione · in attesa di setup");
    const anno = stagioneAttivaInfo.anno;
    const stato = stagioneAttivaInfo.stato;
    if (stato === "preparazione") return tr("Stagione {{anno}} · in preparazione", { anno });
    if (stato === "mercato_estivo") return tr("Stagione {{anno}} · mercato estivo", { anno });
    if (stato === "lista_provvisoria") return tr("Stagione {{anno}} · lista provvisoria", { anno });
    if (stato === "mercato_invernale") return tr("Stagione {{anno}} · mercato invernale", { anno });
    if (stato === "conclusa") return tr("Stagione {{anno}} · conclusa", { anno });
    // in_corso: aggiungiamo il dettaglio giornata se conosciuto
    if (giornataInCorso) {
      return tr("Stagione {{anno}} · giornata {{g}} (SA {{sa}}) · in corso", {
        anno,
        g: giornataInCorso.numero,
        sa: giornataInCorso.numero_serie_a,
      });
    }
    if (prossimaGiornata) {
      return tr("Stagione {{anno}} · giornata {{g}} (SA {{sa}})", {
        anno,
        g: prossimaGiornata.numero,
        sa: prossimaGiornata.numero_serie_a,
      });
    }
    if (ultimaGiornataCalcolata) {
      return tr("Stagione {{anno}} · dopo giornata {{g}}", {
        anno,
        g: ultimaGiornataCalcolata.numero,
      });
    }
    return tr("Stagione {{anno}} · in corso", { anno });
  })();

  return (
    <MobileShell>
      {/* Greeting */}
      <div>
        <div className="font-mono text-[10px] text-fg-muted uppercase tracking-[0.15em] mb-1">
          {stagioneLabel}
        </div>
        <div className="font-display text-3xl text-fg tracking-wide">
          {tr("Ciao, {{nome}} 👋", { nome: firstName })}
        </div>
      </div>

      {/* Banner vittoria — celebra il vincitore dell'ultima stagione conclusa.
          Si auto-nasconde se non c'e' stagione conclusa o se non ci sono
          vincitori. Coriandoli al primo accesso post-vittoria. */}
      <VittoriaBanner />

      {/* Andrea 2026-10-08: su telefono, come avere FantaSquad tra le app */}
      <InstallaAppBanner />

      {/* Mia squadra + Budget (Andrea 2026-06-11): titolo come gli altri,
          card cliccabile → Gestione Rosa, riga allineata logo+maglia (sx) ·
          nome (centro) · budget (dx). */}
      {miaSquadra ? (
        <div>
          <h3 className="font-display text-lg tracking-wider text-fg mb-2">
            {tr("LA MIA SQUADRA")}
          </h3>
          <Link
            to="/rosa"
            className="block relative bg-gradient-to-br from-primary to-primary-deep rounded-card p-4 overflow-hidden shadow-hero hover:ring-2 hover:ring-accent/40 transition"
          >
            {/* Nome — SOLO su telefono: in alto a sinistra (su desktop va al
                centro nella riga sotto). Andrea 2026-06-11. */}
            <div className="relative sm:hidden mb-3">
              <div className="font-display text-xl text-accent titolo-riquadro flex items-center gap-1.5">
                <span className="truncate">{miaSquadra.nome}</span>
                <TrofeiSquadra squadraId={miaSquadra.id} size="sm" />
              </div>
              <div className="text-[11px] text-fg-muted mt-0.5 truncate">{posLabel}</div>
            </div>

            <div className="relative flex items-center justify-between gap-3">
              {/* Sinistra: logo + maglia */}
              <div className="flex items-center gap-2 shrink-0">
                <LogoSquadra squadraId={miaSquadra.id} size={80} />
                <MagliaSquadra squadraId={miaSquadra.id} size={80} />
              </div>
              {/* Centro: nome + posizione — solo da tablet/desktop in su */}
              <div className="hidden sm:block flex-1 min-w-0 text-center">
                <div className="font-display text-xl text-accent titolo-riquadro flex items-center justify-center gap-1.5">
                  <span className="truncate">{miaSquadra.nome}</span>
                  <TrofeiSquadra squadraId={miaSquadra.id} size="sm" />
                </div>
                <div className="text-[11px] text-fg-muted mt-0.5 truncate">{posLabel}</div>
              </div>
              {/* Destra: budget */}
              <div className="text-right shrink-0">
                <div className="font-mono text-[9px] tracking-[0.12em] uppercase text-accent">
                  {tr("Budget disponibile")}
                </div>
                <div className="font-display text-2xl leading-none tracking-wide text-fg">
                  {saldoMilioni === null ? "—" : saldoMilioni.toFixed(2)}
                  <span className="text-xs font-sans text-fg-muted ml-1">{tr("mln")}</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      ) : (
        <BudgetHeroCard saldoMilioni={saldoMilioni} />
      )}

      {/* Andamento — ultime 5 di campionato e di coppa (Andrea 2026-09-09).
          Click → Calendario per i risultati nel dettaglio. */}
      {miaSquadra && (
        <AndamentoCard
          squadraId={miaSquadra.id}
          giornate={giornate ?? []}
          competizioni={competizioniStagione ?? []}
          nomeSquadra={nomeSquadraById}
        />
      )}

      {/* Banner tr("Incontro concluso") — sopra prossima sfida, click → /partita/:id.
          Andrea 2026-09-11: resta finché non parte il turno dopo, poi il
          posto è del blocco "in corso". Andrea 2026-09-17: coi loghi delle
          due squadre sopra i nomi, come in tr("Prossima sfida").
          Christian 2026-11-03: tutte le partite della giornata (anche coppa). */}
      {!giornataInCorso && miaSquadra && (
        <IncontroConcluso
          partite={mieConcluse}
          squadraId={miaSquadra.id}
          numeroGiornata={ultimaGiornataCalcolata?.numero ?? null}
          nomeSquadra={nomeSquadraById}
          nomeCompetizione={nomeCompetizioneById}
        />
      )}

      {/* Giornata in corso (Andrea 2026-09-11): dalla deadline al calcolo la
          partita di questa giornata resta qui col bottone tr("Vai al live"),
          mentre tr("Prossima sfida") passa al turno successivo. */}
      {miaSquadra && giornataInCorso && (
        <ProssimaSfida
          modalita="live"
          squadraId={miaSquadra.id}
          giornata={giornataInCorso}
          competizioni={competizioniInCorso}
          nomeSquadra={nomeSquadraById}
          logoSquadra={logoSquadraById}
          messaggioVuoto=""
        />
      )}

      {/* Prossima sfida + countdown consegna. Andrea 2026-09-09: se nella
          stessa giornata si gioca anche la coppa, il blocco si sfoglia con
          le frecce e il countdown segue la competizione mostrata. */}
      {miaSquadra ? (
        <ProssimaSfida
          squadraId={miaSquadra.id}
          giornata={prossimaGiornata}
          inAttesa={!!giornataInCorso}
          competizioni={competizioniInCorso}
          nomeSquadra={nomeSquadraById}
          logoSquadra={logoSquadraById}
          messaggioVuoto={
            !stagioneAttivaInfo
              ? tr("Nessuna stagione attiva.")
              : !primaCompetizione
                ? tr(
                    tr(
                      "Nessuna competizione creata. L'admin deve crearne una da /admin/competizioni.",
                    ),
                  )
                : !prossimaGiornata
                  ? tr("Nessuna giornata da giocare.")
                  : tr("Nessuna sfida trovata per la tua squadra nella prossima giornata.")
          }
        />
      ) : (
        <div>
          <h3 className="font-display text-lg tracking-wider text-fg mb-2">
            {tr("PROSSIMA SFIDA")}
          </h3>
          <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center">
            <div className="text-4xl mb-2">⚽</div>
            <div className="text-fg-muted text-sm">
              {tr("Devi essere presidente di una squadra.")}
            </div>
          </div>
        </div>
      )}

      {/* Classifica — sfogliabile per competizione (campionato + gironi
          CL) come tr("Prossima sfida"). Andrea 2026-09-09. */}
      <ClassificaCard
        competizioni={competizioniInCorso}
        squadre={squadre ?? []}
        userId={user?.id}
      />

      {/* Centro scommesse — blocco dedicato sotto la classifica
          (Andrea 2026-06-13). Intero blocco cliccabile → /scommesse. */}
      <div>
        <Link
          to="/scommesse"
          className="block relative bg-gradient-to-br from-primary to-primary-deep rounded-card p-4 overflow-hidden shadow-hero hover:ring-2 hover:ring-accent/40 transition"
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl shrink-0">🎰</div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg text-accent titolo-riquadro tracking-wide">
                {tr("Squad Bet")}
              </div>
              <div className="text-[11px] text-fg-muted">
                {scommesseAperte > 0
                  ? tr(
                      scommesseAperte === 1
                        ? tr("{{n}} partita aperta · sfida i presidenti o punta col banco")
                        : tr("{{n}} partite aperte · sfida i presidenti o punta col banco"),
                      { n: scommesseAperte },
                    )
                  : tr("Sfida i presidenti o punta col banco")}
              </div>
            </div>
            {scommesseAperte > 0 && (
              <span className="shrink-0 bg-accent text-ink text-xs font-bold px-2 py-0.5 rounded-pill font-mono">
                {scommesseAperte}
              </span>
            )}
            <div className="shrink-0 text-accent text-xl">→</div>
          </div>
        </Link>
      </div>

      {/* Gazzetta dello Squad — la stampa della lega, blocco gemello del
          centro scommesse (Andrea 2026-09-24). Intero blocco → /gazzetta. */}
      <div>
        <Link
          to="/gazzetta"
          className="block relative bg-gradient-to-br from-primary to-primary-deep rounded-card p-4 overflow-hidden shadow-hero hover:ring-2 hover:ring-accent/40 transition"
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl shrink-0">📰</div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg text-accent titolo-riquadro tracking-wide">
                {tr("Squad Magazine")}
              </div>
              <div className="text-[11px] text-fg-muted">
                {tr("Prime pagine, report, dichiarazioni e il Top 11 della giornata")}
              </div>
            </div>
            <div className="shrink-0 text-accent text-xl">→</div>
          </div>
        </Link>
      </div>

      {/* Shortcut */}
      <div>
        <h3 className="font-display text-lg tracking-wider text-fg mb-2">{tr("SHORTCUT")}</h3>
        <div className="grid grid-cols-3 gap-2">
          <Link
            to="/privacy"
            className="bg-bg-card border border-primary/20 rounded-card p-3 text-center text-xs hover:bg-bg-card-hover transition"
          >
            <div className="text-xl mb-1">🔐</div>
            <div className="font-bold">{tr("Privacy")}</div>
          </Link>
          {isAdminCapable ? (
            <Link
              to="/gestione"
              className="bg-bg-card border border-accent/30 rounded-card p-3 text-center text-xs hover:bg-bg-card-hover transition"
            >
              <div className="text-xl mb-1">⚙️</div>
              <div className="font-bold text-accent">{tr("Gestione")}</div>
            </Link>
          ) : (
            <Link
              to="/notifiche"
              className="bg-bg-card border border-primary/20 rounded-card p-3 text-center text-xs hover:bg-bg-card-hover transition"
            >
              <div className="text-xl mb-1">🔔</div>
              <div className="font-bold">{tr("Notifiche")}</div>
            </Link>
          )}
          <Link
            to="/tesoreria"
            className="bg-bg-card border border-primary/20 rounded-card p-3 text-center text-xs hover:bg-bg-card-hover transition"
          >
            <div className="text-xl mb-1">💰</div>
            <div className="font-bold">{tr("Tesoreria")}</div>
          </Link>
        </div>
      </div>

      {/* Social */}
      <div className="flex justify-center gap-3 pt-1">
        <a
          href="https://www.tiktok.com/@fantasquad.it"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-bold bg-bg-card border border-primary/20 rounded-card px-4 py-2 hover:bg-bg-card-hover transition"
        >
          🎵 TikTok
        </a>
        <a
          href="https://www.instagram.com/fantasquad.it"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-bold bg-bg-card border border-primary/20 rounded-card px-4 py-2 hover:bg-bg-card-hover transition"
        >
          📸 Instagram
        </a>
      </div>

      {/* Footer info */}
      <div className="text-center pt-2 space-y-0.5">
        {version && (
          <p className="text-[10px] text-fg-muted font-mono">FantaSquad v{version.version}</p>
        )}
        <p className="text-[10px] text-fg-muted">
          {tr("© {{anno}} FantaSquad · Tutti i diritti riservati", {
            anno: new Date().getFullYear(),
          })}
        </p>
      </div>
    </MobileShell>
  );
}
